import { useState } from "react";
import { Shield, ArrowRight, Activity, Lock } from "lucide-react";
import { toast } from "sonner";
import { useTenant } from "@/context/TenantContext";
import { api } from "@/lib/api";

const SEVERITY_COLOR = {
    high: { bg: "#7a2a1e22", border: "#e07a5f88", text: "#e07a5f" },
    medium: { bg: "#d4a04b22", border: "#d4a04b88", text: "#d4a04b" },
    low: { bg: "#7b8c4922", border: "#7b8c4988", text: "#a8b577" },
};

export default function StarMap() {
    const { tenant } = useTenant();
    const [form, setForm] = useState({ domain: "", company: "", notes: "" });
    const [scan, setScan] = useState(null);
    const [running, setRunning] = useState(false);

    const accent = tenant?.accent || "#D4A04B";
    const baseColor = tenant?.color || "#1A432B";
    const stateName = tenant?.state_name || "EIGOO";

    const onScan = async (e) => {
        e.preventDefault();
        if (!form.domain.trim()) {
            toast.error("Domain required");
            return;
        }
        setRunning(true);
        setScan(null);
        try {
            const { data } = await api.post("/hub/star-map/scan", form);
            setScan(data.scan);
            toast.success("Scan complete · posture mapped");
        } catch {
            toast.error("Scan failed");
        } finally {
            setRunning(false);
        }
    };

    return (
        <div
            className="min-h-screen text-cream"
            style={{ background: baseColor }}
            data-testid="star-map-page"
        >
            <section className="relative max-w-6xl mx-auto px-5 md:px-10 pt-16 pb-24">
                <span className="divider-rule" style={{ color: accent }}>
                    <Activity size={12} /> Star-Map Analyzer · {stateName}
                </span>
                <h1 className="font-serif text-5xl md:text-6xl lg:text-7xl leading-[0.98] mt-6">
                    Map your{" "}
                    <span className="italic" style={{ color: accent }}>
                        cyber posture.
                    </span>
                </h1>
                <p className="mt-6 max-w-xl text-cream/70 leading-relaxed">
                    A 90-second scan of your public surface. We grade posture,
                    surface findings, and route the report to your{" "}
                    {tenant?.wayfinder_name || "EIGOO Wayfinder"}.
                </p>

                <form
                    onSubmit={onScan}
                    className="mt-12 grid md:grid-cols-12 gap-4 p-7 rounded-sm border"
                    style={{
                        borderColor: `${accent}55`,
                        background: "rgba(0,0,0,0.18)",
                    }}
                    data-testid="star-map-form"
                >
                    <input
                        type="text"
                        placeholder="yourdomain.com"
                        value={form.domain}
                        onChange={(e) =>
                            setForm((s) => ({ ...s, domain: e.target.value }))
                        }
                        className="md:col-span-5 bg-transparent border-b border-cream/30 py-3 px-2 outline-none text-cream placeholder:text-cream/35 font-serif text-lg"
                        data-testid="star-map-domain"
                    />
                    <input
                        type="text"
                        placeholder="Company name"
                        value={form.company}
                        onChange={(e) =>
                            setForm((s) => ({ ...s, company: e.target.value }))
                        }
                        className="md:col-span-4 bg-transparent border-b border-cream/30 py-3 px-2 outline-none text-cream placeholder:text-cream/35"
                    />
                    <button
                        type="submit"
                        disabled={running}
                        className="md:col-span-3 btn-pill justify-center disabled:opacity-50"
                        style={{
                            background: accent,
                            color: baseColor,
                            border: `1px solid ${accent}`,
                        }}
                        data-testid="star-map-run-btn"
                    >
                        {running ? (
                            "Scanning…"
                        ) : (
                            <>
                                <Lock size={14} /> Run Scan{" "}
                                <ArrowRight size={14} />
                            </>
                        )}
                    </button>
                </form>

                {scan && (
                    <div
                        className="mt-12 grid md:grid-cols-12 gap-6"
                        data-testid="star-map-result"
                    >
                        <aside
                            className="md:col-span-5 p-8 rounded-sm border text-center"
                            style={{
                                borderColor: `${accent}55`,
                                background: "rgba(0,0,0,0.25)",
                            }}
                        >
                            <p
                                className="text-[0.65rem] tracking-[0.3em] uppercase"
                                style={{ color: accent }}
                            >
                                Posture Score
                            </p>
                            <p
                                className="font-serif text-[6rem] leading-none mt-3"
                                style={{ color: accent }}
                                data-testid="posture-score"
                            >
                                {scan.posture_score}
                                <span className="text-cream/50 text-2xl">
                                    /100
                                </span>
                            </p>
                            <p className="mt-4 text-sm text-cream/70">
                                {scan.domain}
                            </p>
                            <div
                                className="mt-6 pt-6 border-t"
                                style={{ borderColor: `${accent}33` }}
                            >
                                <Shield
                                    size={28}
                                    className="mx-auto"
                                    style={{ color: accent }}
                                />
                                <p className="text-sm mt-3 text-cream/80 leading-relaxed">
                                    {scan.recommendation}
                                </p>
                            </div>
                        </aside>

                        <div className="md:col-span-7">
                            <p
                                className="text-[0.65rem] tracking-[0.3em] uppercase"
                                style={{ color: accent }}
                            >
                                Findings · {scan.findings.length}
                            </p>
                            <h3 className="font-serif text-3xl mt-2">
                                Surface Report
                            </h3>
                            <ul
                                className="mt-5 space-y-3"
                                data-testid="star-map-findings"
                            >
                                {scan.findings.map((f, i) => {
                                    const c =
                                        SEVERITY_COLOR[f.severity] ||
                                        SEVERITY_COLOR.low;
                                    return (
                                        <li
                                            key={i}
                                            className="p-4 rounded-sm border flex items-start gap-3"
                                            style={{
                                                background: c.bg,
                                                borderColor: c.border,
                                            }}
                                        >
                                            <span
                                                className="text-[0.6rem] tracking-[0.3em] uppercase font-semibold pt-0.5 shrink-0"
                                                style={{ color: c.text }}
                                            >
                                                {f.severity}
                                            </span>
                                            <span className="text-sm text-cream/85">
                                                {f.title}
                                            </span>
                                        </li>
                                    );
                                })}
                            </ul>
                            {tenant?.wayfinder_id && (
                                <p
                                    className="mt-6 text-[0.65rem] tracking-[0.3em] uppercase text-cream/50"
                                    data-testid="star-map-wayfinder-tag"
                                >
                                    Routed to {tenant.wayfinder_name} ·{" "}
                                    {tenant.wayfinder_id}
                                </p>
                            )}
                        </div>
                    </div>
                )}
            </section>
        </div>
    );
}
